import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {first} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {Operatore, Organizzazione} from '../_models/_Index';
import {OrdiniStat} from '../_models/OrdiniStat';
import {AuthenticationService} from '../_services/authentication.service';
import {OrganizzazioniService} from '../_services/organizzazioni.service';
import {LicenzeService} from '../_services/licenze.service';
import {MenuAccessService} from '../_services/menu-access.service';
import {StatisticheService} from '../_services/statistiche.service';
import {SaveMessage} from '../_helpers/save-message';
import {constants} from '../_helpers/constants';

@Component({
  selector: 'app-dashboard-home',
  templateUrl: './dashboard-home.component.html',
  styleUrls: ['./dashboard-home.component.scss'],
  standalone: false
})
export class DashboardHomeComponent implements OnInit, OnDestroy {

  userName = '';
  firstName = '';
  organizzazioneCodice = '';
  isStandalone = false;
  constants = constants;

  now: Date = new Date();
  loading = false;
  loadError = false;
  ordiniStat?: OrdiniStat;

  /** Tri-state (issue #3169): null = caricamento (nessun flash), false = nessuna org attiva → avviso. */
  hasActiveOrgs: boolean | null = null;

  /** Mappa di accesso alle quick actions. Pre-calcolata e ricalcolata a ogni cambio org/utente. */
  quickActionsAccess: { [key: string]: boolean } = {};

  readonly barChartColors = ['#fbbf24', '#a78bfa', '#22c55e', '#94a3b8'];
  readonly pieChartColors = ['#fbbf24', '#22c55e', '#3b82f6', '#a78bfa', '#ef4444', '#06b6d4', '#f97316', '#ec4899'];

  private subscription = new Subscription();
  private clockHandle: any;

  constructor(
    public authService: AuthenticationService,
    public licenseService: LicenzeService,
    private menuAccess: MenuAccessService,
    private route: ActivatedRoute,
    private organizzazioniService: OrganizzazioniService,
    private statisticheService: StatisticheService,
    private saveMessage: SaveMessage) { }

  get greeting(): string {
    const h = this.now.getHours();
    if (h < 13) return 'Buongiorno';
    if (h < 19) return 'Buon pomeriggio';
    return 'Buonasera';
  }

  get ordiniLink(): string[] {
    return ['/ordini', 'inviate'];
  }

  get hasChartData(): boolean {
    return (this.ordiniStat?.eventiLastMonth?.length ?? 0) > 0
      || (this.ordiniStat?.risorseOfToday?.length ?? 0) > 0;
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params.id_token == null) {
        return false;
      }
      this.authService.externalLogin(params.id_token, params.refresh_token)
        .pipe(first())
        .subscribe(
          data => {
            if (data && data.organizzazioneId) {
              void Promise.all([
                this.organizzazioniService.setCurrentOrganizzazioneValueById(data.organizzazioneId),
                this.licenseService.ensureModuliOfOrgLoadedAsync(data.organizzazioneId)
              ]).catch(() => {
                this.saveMessage.messageError('Errore nel caricamento di organizzazione e licenze.');
              });
            }
          }
        );
    });

    if (this.authService.currentUserValue != null) {
      this.userName = this.authService.currentUserValue.userName;
      this.firstName = this.authService.currentUserValue.firstName ?? this.authService.currentUserValue.userName;
    }

    if (this.authService.currentUserValue?.jwtToken) {
      this.checkActiveOrgs();
    }

    this.subscription.add(this.authService.currentUser.subscribe((_user: Operatore) => {
      if (_user != null) {
        this.userName = (_user.firstName ?? '') + ' ' + (_user.lastName ?? '');
        this.firstName = _user.firstName ?? _user.userName ?? '';
      } else {
        this.userName = '';
        this.firstName = '';
      }
    }));

    this.subscription.add(this.organizzazioniService.currentOrganizzazione.subscribe((_org: Organizzazione) => {
      if (_org) {
        this.organizzazioneCodice = _org.descrizione;
      }
      this.isStandalone = this.organizzazioniService.isStandalone();
      this.computeQuickActionsAccess();
      this.refresh();
      if (this.authService.currentUserValue?.jwtToken) {
        this.checkActiveOrgs();
      }
    }));

    // Ricalcola lo stato delle quick actions ogni volta che cambiano i permessi caricati.
    // Necessario perché al refresh della pagina il subscribe a currentOrganizzazione può
    // scattare PRIMA che le licenze siano state caricate, lasciando tutte le card disabilitate.
    this.subscription.add(this.licenseService.currentPermessi$.subscribe(() => {
      this.computeQuickActionsAccess();
    }));

    this.clockHandle = setInterval(() => this.now = new Date(), 1000);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.clockHandle) {
      clearInterval(this.clockHandle);
    }
  }

  refresh() {
    if (!this.authService.currentUserValue?.jwtToken) {
      this.loading = false;
      this.loadError = false;
      return;
    }

    const orgId = this.organizzazioniService.organizzazioneId;
    if (!orgId) {
      return;
    }
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    this.loading = true;
    this.loadError = false;

    this.statisticheService.getStatistics(start, end, orgId, -1)
      .then((ordini) => { this.ordiniStat = ordini; })
      .catch(() => {
        this.loadError = true;
        this.saveMessage.messageError('Ci sono problemi con il caricamento delle statistiche.');
      })
      .finally(() => this.loading = false);
  }

  /** Verifica se l'operatore ha almeno un'organizzazione attiva (issue #3169). Tri-state per evitare flash. */
  private checkActiveOrgs() {
    this.organizzazioniService.getOrganizzazioniFilteredAsync('', 0, 0, 'OrgName', true)
      .then((result) => this.hasActiveOrgs = (result?.result?.length ?? 0) > 0)
      .catch(() => this.hasActiveOrgs = null);
  }

  /**
   * Calcola la matrice di accesso per ogni quick action della dashboard, dalla stessa fonte che
   * governa il menù laterale: MenuAccessService.can(id), non più una copia locale di modulo/ruoli.
   * Un binding cambiato in Mappatura menù (o la rifinitura per organizzazione) si riflette qui
   * senza bisogno di toccare questo file.
   */
  private computeQuickActionsAccess() {
    const m = this.menuAccess;
    this.quickActionsAccess = {
      avanzamento: m.can('mes-avanzamento'),
      trasferimento: m.can('mes-trasferimento'),
      ordini: m.can('ordini-inviati'),
      ricevimento: m.can('ricevimenti-ddt'),
      terzista: m.can('conto-lavoro-invio'),
      ddtTerzisti: m.can('conto-lavoro-ddt'),
      spedizione: m.can('spedizioni-imballaggio'),
      ordiniClienti: m.can('vendita-ordini-clienti'),
      anagrafiche: m.can('gestione-anagrafiche'),
      risorse: m.can('gestione-risorse'),
      lavorazioni: m.can('gestione-lavorazioni'),
      movimenti: m.can('analisi-movimenti'),
      contenitori: m.can('gestione-contenitori'),
      qualita: m.can('qualita-controlli'),
      pianificazione: m.can('pianificazione-consegne'),
      articoli: m.can('analisi-articoli'),
      eventi: m.can('analisi-eventi'),
      sessioni: m.can('analisi-sessioni'),
      inventario: m.can('inventario-manuale'),
      pesate: m.can('analisi-pesate')
    };
  }
}
